import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import { isIOS, Option } from 'components/Option';
import { sortingTypeLabel } from 'helpers/sortingTypeLabel';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const SORT_BY = 'fullName';

export const defaultLocationId = (locations) =>
  locations.length > 1 ? '' : locations[0]?._id || '';

const draftOf = ({ sortOrder, locationId }) => ({ sortOrder, locationId });

const FiltersDrawer = ({ open, value, showLocations, locations, onApply, onClose }) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(draftOf(value));

  useEffect(() => setDraft(draftOf(value)), [value, open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <Grid container spacing={2} justifyContent="space-between">
        <Grid item xs="auto">
          <Typography variant="h3">Filters</Typography>
        </Grid>
        <Grid item xs="auto">
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <div className={classes.form}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <label htmlFor="sortBy" className={classes.label}>
              <Typography variant="body1" color="textSecondary">
                Sort by:
              </Typography>
              {/* Full Name is the only sortable field, so the select stays uncontrolled. */}
              <TextField
                select
                id="sortBy"
                name="sortBy"
                defaultValue={SORT_BY}
                variant="outlined"
                inputProps={{ 'aria-label': 'Sort by' }}
                SelectProps={{ native: isIOS }}
                fullWidth
              >
                <Option value={SORT_BY}>Full Name</Option>
              </TextField>
            </label>
          </Grid>

          <Grid item xs={12} lg={6}>
            <label htmlFor="sortOrder" className={classes.label}>
              <Typography variant="body1" color="textSecondary">
                Sort from:
              </Typography>
              <TextField
                select
                id="sortOrder"
                name="sortOrder"
                value={draft.sortOrder}
                onChange={(event) => setDraft({ ...draft, sortOrder: event.target.value })}
                variant="outlined"
                inputProps={{ 'aria-label': 'Sort from' }}
                SelectProps={{ native: isIOS }}
                fullWidth
              >
                {sortingTypeLabel(SORT_BY).map(({ label, value: order }) => (
                  <Option key={order} value={order}>
                    {label}
                  </Option>
                ))}
              </TextField>
            </label>
          </Grid>

          {showLocations && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} lg={6}>
                <label htmlFor="locationId" className={classes.label}>
                  <Typography variant="body1" color="textSecondary">
                    Locations:
                  </Typography>
                  {/* displayEmpty keeps the All Locations label visible for the empty value. */}
                  <TextField
                    select
                    id="locationId"
                    name="locationId"
                    value={draft.locationId}
                    onChange={(event) => setDraft({ ...draft, locationId: event.target.value })}
                    variant="outlined"
                    inputProps={{ 'aria-label': 'Locations' }}
                    SelectProps={{ native: isIOS, displayEmpty: true }}
                    fullWidth
                  >
                    {locations.length > 1 && <Option value="">All</Option>}
                    {locations.map(({ _id, name }) => (
                      <Option key={_id} value={_id}>
                        {name}
                      </Option>
                    ))}
                  </TextField>
                </label>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Button
              color="primary"
              onClick={() =>
                setDraft({ sortOrder: 'asc', locationId: defaultLocationId(locations) })
              }
            >
              Reset all filters
            </Button>
          </Grid>
        </Grid>

        <div className={classes.buttonHolder}>
          <Button color="secondary" className={classes.button} onClick={onClose}>
            Cancel
          </Button>

          <div className={classes.verticalDivider} />

          <Button color="primary" className={classes.button} onClick={() => onApply(draft)}>
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

FiltersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  value: PropTypes.shape({
    sortOrder: PropTypes.string,
    locationId: PropTypes.string,
  }).isRequired,
  showLocations: PropTypes.bool,
  locations: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

FiltersDrawer.defaultProps = {
  showLocations: false,
  locations: [],
};

export default FiltersDrawer;
